import type { ResolvedWorkshopImage } from "../../../utils/workshopImages";

/**
 * ============================================================================
 * The workshop image glimpse — ONE definition, two surfaces (D-099).
 * ============================================================================
 *
 * It was a private component inside `./Workshops` while the /workshops index
 * was the only card that previewed photographs. The Home teaser is the second,
 * and the reasoning below is the part that must not be copied: the publish
 * rule, the `alt=""`/`aria-hidden` argument, and the reserved box. D-095 moved
 * `resolvePublishedImages` out of `WorkshopGallery` for exactly this reason one
 * layer down — two copies of a publish rule is how a withheld face reaches a
 * page — and a second copy of the ALT rule is how a card starts announcing
 * three photo captions inside its own link name.
 *
 * WHAT IT NEVER DECIDES: which photographs are publishable. It is handed a
 * `ResolvedWorkshopImage[]`, which by construction is the subset that is
 * cleared to publish AND has a binary behind it (`workshopImages.ts`). This
 * component cannot be made to show a withheld frame by editing it.
 *
 * THE THUMBNAILS ARE `alt=""` AND THE STRIP IS `aria-hidden`, DELIBERATELY.
 * Both call sites render a card that is ONE link in the accessibility tree —
 * a heading link stretched over the card with `after:absolute after:inset-0` —
 * and an image with alt text inside a link is appended to that link's
 * accessible name. A screen reader would read the workshop title followed by
 * three photo captions before saying "link". The captions are not lost: the
 * detail route renders every one of these images with its full `alt` from the
 * layer, which is where a reader who wants them is going.
 *
 * EVERY FRAME RESERVES ITS BOX. `aspect-[4/3]` rather than a bare height: a
 * replaced element with no declared ratio is 0-height before decode and full
 * height after, which moves everything below it (D-083 measured 179.7–289.1 px
 * of that on the hero).
 *
 * THE REMAINDER IS A TILE, NOT A LINE OF TEXT. It occupies one cell of the same
 * grid at the same ratio, so a card with more photographs than it can show ends
 * on a full row rather than on a ragged "+4 more" floating beside three
 * pictures. It is the only number this component prints and it is the length of
 * the list it was handed minus what it drew — arithmetic about what is on
 * screen, not a claim about the record, which is the line `<Figure>` draws
 * (D-095).
 *
 * ----------------------------------------------------------------------------
 * EVERY PROP IS REQUIRED, INCLUDING THE TWO CLASS STRINGS
 * ----------------------------------------------------------------------------
 * D-093: an undefaulted optional prop interpolated into a `className` ships the
 * string `undefined` into the built HTML — 1 of 3,322 class attributes, painting
 * nothing and indistinguishable from a class name lost in a refactor. Making
 * them required removes the branch rather than defending it.
 *
 * The two surfaces genuinely want different geometry — a teaser card is ~366 px
 * wide beside a ~1180 px index card — so the LAYOUT is the call site's and the
 * rules above are this file's. That is the same split `education.module.css`
 * documents: shared material, local layout.
 *
 * **`frameClassName` CARRIES GEOMETRY ONLY, AND THE BORDERS ARE THIS FILE'S.**
 * The first draft had the call site pass `border border-[var(--border)]` and
 * the remainder tile append `border-[var(--cat-indigo-border)]` after it. Two
 * arbitrary-value utilities of identical specificity, and which one paints is
 * decided by the order Tailwind happens to emit them in — NOT by their order in
 * the attribute. That is D-098 exactly: `.node img` and `.logo-plate img` were
 * both (0,1,1) and bundle order sent Experience's marks to 19 px. A conflict
 * that can only be resolved by generated-file order is not a style choice, so
 * there is no conflict: the frame border and the tile border are declared here,
 * once each, on different elements.
 */
const WorkshopGlimpse = ({
	images,
	skip,
	max,
	className,
	frameClassName
}: {
	images: ResolvedWorkshopImage[];
	/** Frames already shown elsewhere on the card — the teaser's media band. */
	skip: number;
	max: number;
	className: string;
	frameClassName: string;
}): JSX.Element | null => {
	const rest: ResolvedWorkshopImage[] = images.slice(skip);
	if (rest.length === 0) return null;

	const shown: ResolvedWorkshopImage[] = rest.slice(0, max);
	const remaining: number = rest.length - shown.length;

	return (
		<div aria-hidden="true" className={className}>
			{shown.map((entry: ResolvedWorkshopImage) => (
				<span key={entry.image.id} className={`${frameClassName} border border-[var(--border)]`}>
					<img
						src={entry.binary}
						alt=""
						loading="lazy"
						decoding="async"
						className="block h-full w-full object-cover"
					/>
				</span>
			))}
			{remaining > 0 && (
				<span
					className={`${frameClassName} grid place-items-center border border-dashed border-[var(--cat-indigo-border)] bg-[var(--cat-indigo-fill)] text-[var(--cat-indigo-text)]`}
				>
					{/* A COUNT ALONE LEAVES A FULL GRID CELL LOOKING EMPTY. At the
					    index card's width that cell is ~250x188 and read as a
					    failed image rather than as a deliberate tile — the word
					    under the number is what makes it a label. Dashed, because
					    a solid frame at the same weight as the photographs beside
					    it claims to BE one. */}
					<span className="text-center leading-tight">
						<span className="block text-lg font-bold tabular-nums">+{remaining}</span>
						<span className="block text-[10px] font-semibold uppercase tracking-[0.14em]">more</span>
					</span>
				</span>
			)}
		</div>
	);
};

export default WorkshopGlimpse;
